import React, { useMemo } from 'react';
import { ErrorMessage, Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { SongCollectionApiModel } from '@round/api';
import { StylesConfig, ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../../App.types';
import Button from '../../../../ui/Buttons/Button/Button';
import * as yup from 'yup';
import FormField from '../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../ui/DataEntry/Label/Label';
import styles from './SongCollectionForm.module.css';
import Select from '../../../../ui/DataEntry/Select/Select';
import useNonNullContext from '../../../../Hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/Options';
import FileUpload from '../../../../ui/DataEntry/FileUpload/FileUpload';
import FileUploadImagePreview from '../../../../ui/DataEntry/FileUpload/FileUploadImagePreview/FileUploadImagePreview';

type SongCollectionFormValues = Omit<SongCollectionApiModel, 'client'> & {
    client: ValueType<GenericDropdownOption<number>, false>;
    image: File[];
};

export type SongCollectionFormProps = {
    handleSubmit: (values: SongCollectionFormValues, helpers: FormikHelpers<SongCollectionFormValues>) => void;
    initialValues?: SongCollectionFormValues;
    renderActions?: (props: FormikProps<SongCollectionFormValues>) => React.ReactNode;
};

const defaultInitialValues: SongCollectionFormProps['initialValues'] = {
    client: null,
    name: '',
    image: [],
};

const defaultRenderActions: SongCollectionFormProps['renderActions'] = ({ submitForm, isSubmitting, isValid }) => (
    <Button
        className={styles.submitButton}
        type="filled"
        color="black"
        onClick={submitForm}
        loading={isSubmitting}
        disabled={!isValid}
    >
        Submit
    </Button>
);

const validationSchema = yup.object({
    client: yup.object().required('Client is required'),
    name: yup.string().required('Name is required'),
});

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
    }),
    valueContainer: (base, state) => ({
        ...base,
        fontSize: '0.8125rem',
        padding: state.isMulti ? '0.625rem 1rem 0.4375rem 1rem' : '0.625rem 1rem',
    }),
    multiValue: (base) => ({
        ...base,
        margin: '0 3px 3px 0',
    }),
    multiValueRemove: (base) => ({
        ...base,
        height: '1rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 1000,
    }),
};

const SongCollectionForm = ({
    handleSubmit,
    renderActions = defaultRenderActions,
    initialValues = defaultInitialValues,
}: SongCollectionFormProps) => {
    const { clients } = useNonNullContext(OptionsContext);

    const clientsOptions: GenericDropdownOption<number>[] = useMemo(
        () =>
            clients.map((c) => ({
                value: c.id,
                label: c.name,
            })),
        [clients]
    );

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {(props) => (
                <>
                    <Form>
                        <FormField>
                            <Label htmlFor="name">Name</Label>
                            <Field id="name" name="name" placeholder="Enter name" />
                            <ErrorMessage name="name">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="client">Client</Label>
                            <Field name="client">
                                {(props: FieldProps<SongCollectionFormValues['client']>) => (
                                    <Select
                                        id="client"
                                        styles={selectStyles}
                                        placeholder="Select client"
                                        menuPortalTarget={document.body}
                                        options={clientsOptions}
                                        value={props.field.value}
                                        onChange={(value) => {
                                            props.form.setFieldValue(props.field.name, value);
                                            props.form.setFieldTouched(props.field.name, true, true);
                                        }}
                                    />
                                )}
                            </Field>
                            <ErrorMessage name="client">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>

                        <FormField>
                            <Label htmlFor="image">Image</Label>
                            <Field name="image">
                                {(props: FieldProps<SongCollectionFormValues['image']>) => (
                                    <FileUpload
                                        files={props.field.value}
                                        onUpload={(files) => props.form.setFieldValue(props.field.name, files)}
                                    >
                                        {({ files: [image] }) => (
                                            <FileUploadImagePreview className={styles.imagePreview} file={image} />
                                        )}
                                    </FileUpload>
                                )}
                            </Field>
                        </FormField>
                    </Form>
                    {renderActions(props)}
                </>
            )}
        </Formik>
    );
};

export default SongCollectionForm;
